import React, { useState, useMemo } from "react";
import swal from "sweetalert2";
import styles from "../../styles/TutorForm.module.css";
import { saveFormSizesData } from "../../services/api";
import MissingSizeAlert from './MissingSizeAlert';

const CommonForm = ({
  data,
  nicknameTooltipText,
  initialsTooltipText,
  onSuccessSubmit,
  isMobileView
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [formRows, setFormRows] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customers = useMemo(() => {
    return (data.customers || [])
      .filter(customer => customer && customer.items)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    setSelectedCustomerId(id);
  
    if (!id) {
      setFormRows([]);
      return;
    }
  
    const customer = customers.find((c) => c.id === parseInt(id));
    if (!customer || !customer.items) {
      setFormRows([]);
      return;
    }
  
    const allRows = customer.items.flatMap((item) => {
      return Array.from({ length: item.pending_sizes || 0 }, () => ({
        customerId: customer.id,
        itemId: item.id,
        itemName: item.name,
        alias: item.alias,
        size: "",
        nickname: "",
        initials: "",
        sizesAvailable: item.available_sizes || [],
      }));
    });
  
    setFormRows(allRows);
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...formRows];

    if (field === "size") {
      const selectedSize = updated[index].sizesAvailable.find(
        (s) => s.code === value
      );
      updated[index].size = value;
      updated[index].width = selectedSize?.width || null;
      updated[index].height = selectedSize?.height || null;
    } else {
      updated[index][field] = value;
    }

    setFormRows(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const customer = customers.find(
      (c) => c.id === parseInt(selectedCustomerId)
    );
    if (!customer) {
      swal.fire({
        title: "Error",
        text: "Debe seleccionar un alumno.",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#0071ce",
      });
      return;
    }

    const rowsToPreview = formRows
      .filter((row) => row.size !== "")
      .map((row) => {
        return {
          name: customer.name,
          nickname: row.nickname,
          initials: row.initials,
          size: row.size,
          width: row.width,
          height: row.height,
          order_item_id: row.itemId,
          customer_id: row.customerId,
        };
      });

    if (rowsToPreview.length === 0) {
      swal.fire({
        title: "Error",
        text: "Debe completar al menos un talle.",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#0071ce",
      });
      return;
    }

    setPreviewData(rowsToPreview);
    setIsPreviewing(true);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await saveFormSizesData(previewData);
      console.log("Respuesta del servidor:", response);

      setTimeout(() => {
        swal.fire({
          title: "Éxito",
          text: "Los datos fueron enviados correctamente.",
          icon: "success",
          confirmButtonColor: "#0071ce",
        });

        setIsPreviewing(false);
        setIsSubmitting(false);
        setFormRows((prevRows) =>
          prevRows.filter(
            (row) =>
              !previewData.some(
                (sent) =>
                  sent.customer_id === row.customerId &&
                  sent.order_item_id === row.itemId &&
                  sent.size === row.size &&
                  sent.nickname === row.nickname &&
                  sent.initials === row.initials
              )
          )
        );
        if (typeof onSuccessSubmit === "function") {
          onSuccessSubmit();
        }
      }, 1000);
    } catch (error) {
      console.error("Error al enviar datos:", error);
      setIsSubmitting(false);

      swal.fire({
        title: "Error",
        text: "Hubo un problema al enviar los datos. Por favor, intentá de nuevo.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const goBackToForm = () => {
    setIsPreviewing(false);
  };

  // Orden del talle (la lista ya viene de menor a mayor); sin talle → al final.
  const sizeOrderIndex = (row) => {
    if (!row.size) return Number.POSITIVE_INFINITY;
    const idx = (row.sizesAvailable || []).findIndex((s) => s.code === row.size);
    return idx === -1 ? Number.POSITIVE_INFINITY : idx;
  };

  // Filas agrupadas por tipo de prenda (no mezclar) + ordenadas por talle a medida que
  // se cargan. Conservamos el índice original (_idx) para handleRowChange.
  const buildGroups = () => {
    const map = new Map();
    formRows.forEach((row, index) => {
      const key = `${row.itemName}__${row.alias || ""}`;
      if (!map.has(key)) {
        map.set(key, { key, itemName: row.itemName, alias: row.alias, rows: [] });
      }
      map.get(key).rows.push({ ...row, _idx: index });
    });
    const groups = [...map.values()];
    groups.forEach((g) => g.rows.sort((a, b) => sizeOrderIndex(a) - sizeOrderIndex(b)));
    return groups;
  };

  const hasPendingSizes = useMemo(() => {
    return data.customers.some(customer => 
      customer && customer.items && customer.items.some(item => item.pending_sizes > 0)
    );
  }, [data]);

  if (!hasPendingSizes) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateContent}>
          <i className="bi bi-emoji-frown" style={{ fontSize: "2.5rem", color: "#0071ce" }}></i>
          <h3>No hay alumnos para cargar talles</h3>
          <p>Posibles razones:</p>
          <ul>
            <li>Todos los talles ya fueron cargados</li>
            <li>Ningún alumno seleccionó prendas</li>
          </ul>
        </div>
        <MissingSizeAlert />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <MissingSizeAlert />
      {isMobileView && (
        <div className={styles.mobileHelpSection}>
          <button 
            type="button" 
            onClick={nicknameTooltipText}
            className={styles.mobileHelpButton}
          >
            <i className="bi bi-question-circle"></i> Cómo usar el apodo
          </button>
          <button 
            type="button" 
            onClick={initialsTooltipText}
            className={styles.mobileHelpButton}
          >
            <i className="bi bi-question-circle"></i> Cómo usar personalizado
          </button>
        </div>
      )}
      {!isPreviewing && (
        <>
          <div className={styles.inputGroup}>
            <label>Nombre:</label>
            <select value={selectedCustomerId} onChange={handleCustomerChange}>
              <option value="">Seleccionar</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {formRows.length > 0 && buildGroups().map((g) => (
            <div key={g.key} style={{ marginBottom: "1.4rem" }}>
              <h4 style={{ margin: "0 0 .4rem", color: "#0071ce", fontWeight: 800 }}>
                {g.itemName} {g.alias ? `(${g.alias})` : ""}
              </h4>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Talle (ancho x alto)</th>
                    <th>
                      Apodo
                      <button
                        type="button"
                        className={styles.infoButton}
                        onClick={nicknameTooltipText}
                      >
                        <i
                          className="bi bi-question-circle"
                          style={{ color: "white" }}
                        ></i>
                      </button>
                    </th>
                    <th>
                      Personalizado
                      <button
                        type="button"
                        className={styles.infoButton}
                        onClick={initialsTooltipText}
                      >
                        <i
                          className="bi bi-question-circle"
                          style={{ color: "white" }}
                        ></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((row) => (
                    <tr key={row._idx}>
                      <td data-label="Talle">
                        <select
                          value={row.size}
                          onChange={(e) =>
                            handleRowChange(row._idx, "size", e.target.value)
                          }
                        >
                          <option value="">Seleccionar</option>
                          {row.sizesAvailable
                            .filter((s) => s.code !== "E")
                            .map((s) => (
                              <option key={s.code} value={s.code}>
                                {s.code}{" "}
                                {s?.width && s?.height
                                  ? `(${s.width}x${s.height}) cm`
                                  : ""}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td data-label="Apodo">
                        <input
                          type="text"
                          value={row.nickname}
                          placeholder="ej. (E20) Nico (E10)"
                          onChange={(e) =>
                            handleRowChange(row._idx, "nickname", e.target.value)
                          }
                        />
                      </td>
                      <td data-label="Personalizado">
                        <input
                          type="text"
                          value={row.initials}
                          placeholder="ej. N.S, HUM, etc."
                          onChange={(e) =>
                            handleRowChange(row._idx, "initials", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {formRows.length > 0 && (
            <button type="submit" className={styles.submitButton}>
              Enviar <i className="bi bi-send-fill"></i>
            </button>
          )}
        </>
      )}

      {isPreviewing && (
        <>
          <h5>Confirma que los siguientes datos son correctos?</h5>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Alumno</th>
                <th>Talle</th>
                <th>Apodo</th>
                <th>Personalizado</th>
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, index) => (
                <tr key={index}>
                  <td data-label="Alumno">{row.name}</td>
                  <td data-label="Talle">{row.size}</td>
                  <td data-label="Apodo">{row.nickname}</td>
                  <td data-label="Personalizado">{row.initials}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.buttonGroup}>
            <button
              onClick={goBackToForm}
              type="button"
              className={styles.cancelButton}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver
            </button>
            <button
              onClick={confirmSubmit}
              type="button"
              className={styles.confirmButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  Enviando...{" "}
                  <i className={`bi bi-arrow-repeat ${styles.spin}`} />
                </>
              ) : (
                <>
                  Confirmar <i className="bi bi-check-circle-fill" />
                </>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default CommonForm;